import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

function Consultas() {
  const [consultaData, setConsultaData] = useState({ cep: '', numero: '' });
  const [resultados, setResultados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTim, setLoadingTim] = useState(false);
  const [error, setError] = useState('');
  const [operadorasPermitidas, setOperadorasPermitidas] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [limiteInfo, setLimiteInfo] = useState(null);
  const [telefoneUsuario, setTelefoneUsuario] = useState({});

  const todasOperadoras = [
    { id: 'vivo', name: 'VIVO', color: '#8B00FF' }, // Roxo
    { id: 'claro', name: 'CLARO', color: '#FF0000' }, // Vermelho
    { id: 'tim', name: 'TIM', color: '#0066cc' }, // Azul (já estava)
    { id: 'desktop', name: 'DESKTOP', color: '#FF0000' }, // Vermelho (vermelho e amarelo - usando vermelho como principal)
    { id: 'gigamais', name: 'GIGA MAIS', color: '#0066FF' }, // Azul (azul e branco - usando azul como principal)
    { id: 'vero', name: 'VERO', color: '#ff69b4' }, // Rosa (já estava)
    { id: 'nio', name: 'NIO', color: '#00CC66' }, // Verde (verde e branco - usando verde como principal)
    { id: 'brisanet', name: 'BRISANET', color: '#FF8C00' }, // Laranja (laranja e branco)
    { id: 'aonet', name: 'AONET', color: '#0066FF', gradient: 'linear-gradient(135deg, #0066FF 0%, #FFD700 50%, #00CC66 100%)' }, // Azul, Amarelo e Verde
    { id: 'ligga', name: 'LIGGA', color: '#FF8C00' }, // Laranja
    { id: 'mhnet', name: 'MHNET', color: '#0066FF' }, // Azul
    { id: 'ampernet', name: 'AMPERNET', color: '#FF0000' }, // Vermelho
    { id: 'zaaz', name: 'ZAAZ', color: '#00A651' }, // Verde
    { id: 'uno', name: 'UNO', color: '#00A3E0' } // Azul Uno
  ];

  useEffect(() => {
    loadUserAndPermissions();
    loadLimiteInfo();
  }, []);

  const loadLimiteInfo = async () => {
    try {
      const response = await axios.get('/consultas/stats');
      setLimiteInfo(response.data);
    } catch (err) {
      console.error('Erro ao carregar informações de limite:', err);
    }
  };

  const loadUserAndPermissions = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUser(user);

        // Buscar operadoras permitidas do backend usando a função getOperadorasPermitidas
        // Sempre buscar do backend para garantir dados atualizados
        try {
          const response = await axios.get('/users/me');
          const currentUserData = response.data;
          
          if (currentUserData) {
            // Atualizar currentUser com dados atualizados do backend
            setCurrentUser(currentUserData);
            // Atualizar localStorage também
            localStorage.setItem('user', JSON.stringify(currentUserData));
            
            // Usar operadoras_permitidas_calculadas se disponível (retornado pela rota /users/me)
            if (currentUserData.operadoras_permitidas_calculadas) {
              setOperadorasPermitidas(currentUserData.operadoras_permitidas_calculadas);
            } else {
              // Fallback: se for admin, usar todas as operadoras
              if (currentUserData.role === 'admin') {
                setOperadorasPermitidas(todasOperadoras.map(op => op.id));
              } else {
                // Fallback: usar operadoras_permitidas direto do usuário
                let operadoras = [];
                if (currentUserData.operadoras_permitidas) {
                  try {
                    operadoras = typeof currentUserData.operadoras_permitidas === 'string'
                      ? JSON.parse(currentUserData.operadoras_permitidas)
                      : currentUserData.operadoras_permitidas;
                    if (!Array.isArray(operadoras)) operadoras = [];
                  } catch (e) {
                    operadoras = [];
                  }
                }
                setOperadorasPermitidas(operadoras);
              }
            }
          } else if (user.role === 'admin') {
            // Se não conseguir buscar do backend mas for admin, usar todas
            setOperadorasPermitidas(todasOperadoras.map(op => op.id));
          }
        } catch (err) {
          console.error('Erro ao carregar operadoras permitidas:', err);
          // Em caso de erro, se for admin usar todas, senão não permitir nenhuma
          if (user.role === 'admin') {
            setOperadorasPermitidas(todasOperadoras.map(op => op.id));
          } else {
            setOperadorasPermitidas([]);
          }
        }
      }
    } catch (err) {
      console.error('Erro ao carregar usuário:', err);
      // Em caso de erro, não permitir nenhuma (mais seguro)
      setOperadorasPermitidas([]);
    }
  };

  // Filtrar operadoras baseado nas permissões
  const operadoras = todasOperadoras.filter(op => 
    currentUser?.role === 'admin' || operadorasPermitidas.includes(op.id)
  );

  // Função auxiliar para normalizar CEP (remove caracteres não numéricos)
  const normalizarCep = (cep) => {
    if (!cep) return null;
    const cepLimpo = String(cep).replace(/\D/g, '');
    if (cepLimpo.length === 0) return null;
    return cepLimpo.padStart(8, '0').substring(0, 8);
  };

  // Função auxiliar para formatar CEP com traço
  const formatarCepComTraco = (cep) => {
    const cepNormalizado = normalizarCep(cep);
    if (!cepNormalizado || cepNormalizado.length !== 8) return null;
    return cepNormalizado.substring(0, 5) + '-' + cepNormalizado.substring(5);
  };

  /**
   * Executa uma requisição axios e, em caso de 502/503, refaz até maxAttempts vezes.
   * Respeita o retry da API: uma nova requisição dá novas tentativas no backend.
   * @param {() => Promise} requestFn - função que retorna a promise da requisição (ex: () => axios.post(...))
   * @param {number} maxAttempts - total de tentativas (default 2 = 1 retry no frontend)
   * @returns {Promise} resultado da requisição
   */
  const requestWithRetry = async (requestFn, maxAttempts = 2) => {
    let lastError;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await requestFn();
      } catch (err) {
        lastError = err;
        const status = err.response?.status;
        const isRetryable = (status === 502 || status === 503) && attempt < maxAttempts;
        if (isRetryable) {
          await new Promise(r => setTimeout(r, 1500));
          continue;
        }
        throw err;
      }
    }
    throw lastError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResultados(null);
    setLoading(true);
    setLoadingTim(true);

    const { cep, numero } = consultaData;
    
    if (!cep || !numero) {
      setError('Por favor, preencha CEP e número');
      setLoading(false);
      setLoadingTim(false);
      return;
    }

    try {
      // Inicializar resultados vazios para todas as operadoras
      const resultadosIniciais = operadoras.map(op => ({
        operadora: op.id,
        operadoraName: op.name,
        operadoraColor: op.color,
        loading: op.id === 'tim', // TIM começa em loading
        success: null,
        data: null,
        error: null
      }));
      setResultados(resultadosIniciais);

      // Função para atualizar resultado específico
      const atualizarResultado = (operadoraId, resultado) => {
        setResultados(prev => prev.map(r => 
          r.operadora === operadoraId ? { ...r, ...resultado } : r
        ));
      };

      // Separar operadoras:
      // - BRISANET: obtém token do backend, faz requisição direta à API
      // - LIGGA, MHNET, AMPERNET: consulta via backend (POST dedicado)
      // - AONET, ZAAZ: usa API key do backend, faz requisição via backend
      // - VIVO: tenta API normal via backend primeiro, se der erro 500 usa resposta MelhorPlano
      // - DESKTOP e GIGAMAIS: usam requisição direta para MelhorPlano
      // - CLARO, TIM, VERO e NIO: usam suas próprias APIs via backend
      const temBrisanet = operadoras.find(op => op.id === 'brisanet');
      const temAonet = operadoras.find(op => op.id === 'aonet');
      const temZaaz = operadoras.find(op => op.id === 'zaaz');
      const temLigga = operadoras.find(op => op.id === 'ligga');
      const operadorasMelhorPlano = operadoras.filter(op => 
        ['desktop', 'gigamais'].includes(op.id)
      );
      const temVivo = operadoras.find(op => op.id === 'vivo');
      const operadorasOutras = operadoras.filter(op =>
        !['desktop', 'gigamais', 'brisanet', 'aonet', 'zaaz', 'ligga', 'mhnet', 'ampernet'].includes(op.id)
      );

      // DESKTOP: consulta via backend em paralelo (não bloqueia as demais)
      const temDesktop = operadorasMelhorPlano.find(op => op.id === 'desktop');
      const promiseDesktop = temDesktop ? (async () => {
        try {
          atualizarResultado('desktop', {
            loading: true,
            success: null,
            data: { message: 'Consultando cobertura...' }
          });
          const desktopRes = await requestWithRetry(() => axios.post('/consultas/desktop', { cep }));
          const d = desktopRes.data;
          atualizarResultado('desktop', {
            success: true,
            data: {
              coverage: d.coverage === true,
              message: d.message || (d.coverage ? 'Cobertura disponível' : 'Sem cobertura disponível'),
              details: d.details || null
            },
            loading: false
          });
          if (d.limite !== undefined) {
            setLimiteInfo(prev => ({
              ...prev,
              limite: d.limite,
              usadas: d.usadas,
              restantes: d.restantes
            }));
          }
        } catch (err) {
          console.error('Erro na consulta DESKTOP:', err);
          atualizarResultado('desktop', {
            success: false,
            error: err.response?.data?.error || err.message || 'Erro ao consultar cobertura Desktop',
            loading: false
          });
        }
      })() : null;

      // MHNET: consulta via backend em paralelo (não bloqueia as demais)
      const temMHNET = operadoras.find(op => op.id === 'mhnet');
      const promiseMHNET = temMHNET ? (async () => {
        try {
          atualizarResultado('mhnet', {
            loading: true,
            success: null,
            data: { message: 'Consultando cobertura...' }
          });
          const mhnetRes = await requestWithRetry(() => axios.post('/consultas/mhnet', { cep, numero }));
          const d = mhnetRes.data;
          atualizarResultado('mhnet', {
            success: true,
            data: {
              coverage: d.coverage === true,
              message: d.message || (d.coverage ? 'Cobertura disponível' : 'Sem cobertura disponível'),
              details: d.details || null
            },
            loading: false
          });
          if (d.limite !== undefined) {
            setLimiteInfo(prev => ({
              ...prev,
              limite: d.limite,
              usadas: d.usadas,
              restantes: d.restantes
            }));
          }
        } catch (err) {
          console.error('Erro na consulta MHNET:', err);
          atualizarResultado('mhnet', {
            success: false,
            error: err.response?.data?.error || err.message || 'Erro ao consultar cobertura MHNET',
            loading: false
          });
        }
      })() : null;

      // AMPERNET: consulta via backend em paralelo (não bloqueia as demais)
      const temAmpernet = operadoras.find(op => op.id === 'ampernet');
      const promiseAmpernet = temAmpernet ? (async () => {
        try {
          atualizarResultado('ampernet', {
            loading: true,
            success: null,
            data: { message: 'Consultando cobertura...' }
          });
          const ampernetRes = await requestWithRetry(() => axios.post('/consultas/ampernet', { cep, numero }));
          const d = ampernetRes.data;
          atualizarResultado('ampernet', {
            success: true,
            data: {
              coverage: d.coverage === true,
              message: d.message || (d.coverage ? 'Cobertura disponível' : 'Sem cobertura disponível'),
              details: d.details || null
            },
            loading: false
          });
          if (d.limite !== undefined) {
            setLimiteInfo(prev => ({
              ...prev,
              limite: d.limite,
              usadas: d.usadas,
              restantes: d.restantes
            }));
          }
        } catch (err) {
          console.error('Erro na consulta AMPERNET:', err);
          atualizarResultado('ampernet', {
            success: false,
            error: err.response?.data?.error || err.message || 'Erro ao consultar cobertura AMPERNET',
            loading: false
          });
        }
      })() : null;

      // Fazer requisição única para MelhorPlano apenas vivo e gigamais (desktop usa backend acima)
      let melhorPlanoResponse = null;
      const temGigamais = operadorasMelhorPlano.find(op => op.id === 'gigamais');
      if (temVivo || temGigamais) {
        try {
          const cepComTraco = formatarCepComTraco(cep);
          if (!cepComTraco) {
            throw new Error('CEP inválido');
          }

          const operadorasParaMelhorPlano = [];
          if (temVivo) operadorasParaMelhorPlano.push('vivo');
          if (temGigamais) operadorasParaMelhorPlano.push('gigamais');

          const response = await requestWithRetry(() =>
            axios.post('/consultas/melhorplano', {
              cep: cepComTraco,
              numero: String(numero).trim(),
              operadoras: operadorasParaMelhorPlano
            })
          );
          
          melhorPlanoResponse = { data: response.data };

          const data = melhorPlanoResponse.data;
          const postcodeRes = data.postcodeRes;

          if (postcodeRes && postcodeRes.providers) {
            // Processar VIVO (guardar resposta para usar se API principal falhar)
            if (temVivo && postcodeRes.providers.vivo) {
              const vivoProvider = postcodeRes.providers.vivo;
              let temCobertura = false;
              let resultado = 'Sem cobertura';

              if (vivoProvider && vivoProvider.data && vivoProvider.data.speed) {
                temCobertura = (vivoProvider.data.speed.max !== 0) || (vivoProvider.data.speed.min !== 0);
                resultado = temCobertura ? 'Cobertura disponível' : 'Sem cobertura';
              }

              melhorPlanoResponse.vivoResult = {
                RESULTADO: resultado,
                temCobertura: temCobertura
              };
            }

            // Processar GIGA MAIS (giga-fibra)
            if (operadorasMelhorPlano.find(op => op.id === 'gigamais')) {
              const gigaFibraProvider = postcodeRes.providers['giga-fibra'];
              let temCobertura = false;
              let message = 'Sem cobertura disponível';

              if (gigaFibraProvider && gigaFibraProvider.data && gigaFibraProvider.data.speed) {
                temCobertura = (gigaFibraProvider.data.speed.max !== 0) || (gigaFibraProvider.data.speed.min !== 0);
                message = temCobertura ? 'Cobertura disponível' : 'Sem cobertura disponível';
              }

              const details = postcodeRes.postcode ? {
                state: postcodeRes.postcode.state,
                city: postcodeRes.postcode.city,
                street: postcodeRes.postcode.street,
                neighborhood: postcodeRes.postcode.neighborhood
              } : null;

              // Registrar consulta no backend
              try {
                const resultado = {
                  coverage: temCobertura,
                  message: message,
                  details: details
                };
                const registroResponse = await axios.post('/consultas/registrar/gigamais', { 
                  cep, 
                  numero, 
                  resultado 
                });
                // Atualizar informações de limite
                if (registroResponse.data.limite !== undefined) {
                  setLimiteInfo({
                    ...limiteInfo,
                    limite: registroResponse.data.limite,
                    usadas: registroResponse.data.usadas,
                    restantes: registroResponse.data.restantes
                  });
                }
              } catch (err) {
                console.error('Erro ao registrar consulta Giga Mais:', err);
              }

              atualizarResultado('gigamais', {
                success: true,
                data: {
                  coverage: temCobertura,
                  message: message,
                  details: details
                },
                loading: false
              });
            }
          }
        } catch (err) {
          console.error('Erro na requisição MelhorPlano:', err);
          if (temVivo) {
            atualizarResultado('vivo', {
              success: false,
              error: 'Erro ao consultar cobertura. Tente novamente.',
              loading: false
            });
          }
          if (temGigamais) {
            atualizarResultado('gigamais', {
              success: false,
              error: 'Erro ao consultar cobertura. Tente novamente.',
              loading: false
            });
          }
        }
      }

      // Fazer requisições para outras operadoras (vivo, claro, tim) normalmente via backend
      const promises = operadorasOutras.map(operadora => {
        const isTim = operadora.id === 'tim';
        const isVivo = operadora.id === 'vivo';
        
        // Fazer requisição para cada operadora (com retry em 502/503)
        return requestWithRetry(() => axios.post(`/consultas/${operadora.id}`, { cep, numero }))
          .then(response => {
            // Verificar se VERO precisa de telefone do usuário
            if (operadora.id === 'vero' && response.data.requiresPhone) {
              atualizarResultado(operadora.id, {
                success: null,
                data: {
                  requiresPhone: true,
                  message: response.data.message,
                  error: response.data.error,
                  cep: response.data.cep,
                  numero: response.data.numero
                },
                loading: false
              });
              return { operadora: operadora.id, success: true, requiresPhone: true };
            }

            // Atualizar informações de limite após consulta bem-sucedida
            if (response.data.limite !== undefined) {
              setLimiteInfo({
                ...limiteInfo,
                limite: response.data.limite,
                usadas: response.data.usadas,
                restantes: response.data.restantes
              });
            }

            // Atualizar resultado imediatamente quando receber resposta
            // Garantir que a resposta da Claro sempre tenha o formato esperado
            let responseData = response.data;
            if (operadora.id === 'claro') {
              // Se a resposta tiver estrutura bruta da API (apiVersion, transactionId, data), extrair apenas o necessário
              if (responseData && responseData.apiVersion && responseData.transactionId && responseData.data) {
                // Se tiver errors, tratar como erro
                if (responseData.data.errors) {
                  const errorCode = responseData.data.errors.code;
                  const errorMessage = responseData.data.errors.message;
                  if (errorCode === '404-001' || errorMessage?.includes('ZipCode notfound')) {
                    responseData = {
                      coverage: false,
                      message: 'CEP NAO EXISTENTE NA OPERADORA CLARO',
                      cep: responseData.data.cep || '',
                      numero: responseData.data.number || ''
                    };
                  } else {
                    responseData = {
                      coverage: false,
                      message: errorMessage || 'Erro na consulta Claro',
                      error: errorMessage || 'Erro na consulta Claro'
                    };
                  }
                } else if (responseData.data.technologies) {
                  // Se tiver technologies, manter apenas os campos necessários
                  responseData = {
                    coverage: responseData.coverage !== undefined ? responseData.coverage : false,
                    message: responseData.message || 'Sem cobertura disponível',
                    cep: responseData.cep || responseData.data.cep || '',
                    numero: responseData.numero || responseData.data.number || '',
                    temFibra: responseData.temFibra,
                    temCable: responseData.temCable,
                    cableVendaLiberada: responseData.cableVendaLiberada,
                    limite: responseData.limite,
                    usadas: responseData.usadas,
                    restantes: responseData.restantes
                  };
                } else {
                  // Se não tiver technologies, tratar como erro
                  responseData = {
                    coverage: false,
                    message: 'Resposta da API inválida',
                    error: 'Resposta da API inválida'
                  };
                }
              }
              // Garantir que sempre temos coverage e message
              if (responseData.coverage === undefined) {
                responseData.coverage = false;
              }
              if (!responseData.message) {
                responseData.message = responseData.coverage ? 'Cobertura disponível' : 'Sem cobertura disponível';
              }
            }
            
            atualizarResultado(operadora.id, {
              success: true,
              data: responseData,
              loading: false
            });
            if (isTim) {
              setLoadingTim(false);
            }
            return { operadora: operadora.id, success: true };
          })
          .catch(err => {
            // Se for Vivo e der erro 500/503, usar resposta do MelhorPlano
            if (isVivo && melhorPlanoResponse && melhorPlanoResponse.vivoResult && 
                (err.response?.status === 500 || err.response?.status === 503)) {
              const vivoResult = melhorPlanoResponse.vivoResult;
              
              // Registrar consulta no backend
              try {
                axios.post('/consultas/registrar/vivo', { 
                  cep, 
                  numero, 
                  resultado: vivoResult.RESULTADO 
                }).then(registroResponse => {
                  // Atualizar informações de limite
                  if (registroResponse.data.limite !== undefined) {
                    setLimiteInfo({
                      ...limiteInfo,
                      limite: registroResponse.data.limite,
                      usadas: registroResponse.data.usadas,
                      restantes: registroResponse.data.restantes
                    });
                  }
                }).catch(regErr => {
                  console.error('Erro ao registrar consulta Vivo:', regErr);
                });
              } catch (regErr) {
                console.error('Erro ao registrar consulta Vivo:', regErr);
              }

              atualizarResultado('vivo', {
                success: true,
                data: {
                  RESULTADO: vivoResult.RESULTADO,
                  fallback_used: true,
                  fallback_message: 'A primeira API da Vivo retornou erro. Foi utilizada uma API alternativa para validar a cobertura.'
                },
                loading: false
              });
              
              return { operadora: operadora.id, success: true };
            }
            
            // Se erro for 403 de permissão, não mostrar o card (remover da lista)
            // Se erro for 403 de permissão, não mostrar o card (remover da lista)
            if (err.response?.status === 403 && 
                (err.response?.data?.error?.includes('não tem permissão') || 
                 err.response?.data?.error?.includes('permissão para consultar'))) {
              // Remover operadora da lista de resultados ao invés de mostrar erro
              setResultados(prev => prev.filter(r => r.operadora !== operadora.id));
              if (isTim) {
                setLoadingTim(false);
              }
              return { operadora: operadora.id, success: false, hidden: true };
            }

            // Atualizar informações de limite se erro for de limite excedido
            if (err.response?.data?.limite !== undefined) {
              setLimiteInfo({
                ...limiteInfo,
                limite: err.response.data.limite,
                usadas: err.response.data.usadas,
                restantes: err.response.data.restantes || 0
              });
            }

            // Atualizar resultado imediatamente em caso de erro (não de permissão)
            atualizarResultado(operadora.id, {
              success: false,
              error: err.response?.data?.error || 'Erro ao realizar consulta',
              loading: false
            });
            if (isTim) {
              setLoadingTim(false);
            }
            return { operadora: operadora.id, success: false };
          });
      });

      // Processar BRISANET em paralelo (obter token e fazer requisição direta)
      const promiseBrisanet = temBrisanet ? (async () => {
        try {
          atualizarResultado('brisanet', {
            loading: true,
            success: null,
            data: { message: 'Aguardando consulta...' }
          });

          // Obter token do backend
          const tokenResponse = await axios.get('/consultas/brisanet/token');
          const token = tokenResponse.data.token;

          if (!token) {
            throw new Error('Token não obtido');
          }

          // Formatar CEP com traço
          const cepComTraco = formatarCepComTraco(cep);
          if (!cepComTraco) {
            throw new Error('CEP inválido');
          }

          // Fazer requisição direta à API Brisanet
          const apiUrl = 'https://vskqus9h9i.execute-api.us-east-1.amazonaws.com/checkcors-authorizer/new-checkcoverage';
          const apiResponse = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Origin': 'https://assinebrisanet.com',
              'Referer': 'https://assinebrisanet.com/internet/fortaleza-ce',
              'Tokenauthorization': token
            },
            body: JSON.stringify({
              bucket: 'assinebrisanet.com',
              cep: cepComTraco
            })
          });

          let apiData;
          const responseText = await apiResponse.text();
          try {
            // A resposta pode vir como string JSON dupla
            if (responseText.startsWith('"')) {
              apiData = JSON.parse(JSON.parse(responseText));
            } else {
              apiData = JSON.parse(responseText);
            }
          } catch (e) {
            throw new Error('Resposta da API não é JSON válido');
          }

          if (apiData.status === 'success' && apiData.data) {
            const coverage = apiData.data.coverage || false;
            const resultado = {
              coverage: coverage,
              message: coverage ? 'Cobertura disponível' : 'Sem cobertura'
            };

            // Registrar consulta no backend
            try {
              const registroResponse = await axios.post('/consultas/registrar/brisanet', {
                cep,
                resultado
              });

              // Atualizar informações de limite
              if (registroResponse.data.limite !== undefined) {
                setLimiteInfo(prev => ({
                  ...prev,
                  limite: registroResponse.data.limite,
                  usadas: registroResponse.data.usadas,
                  restantes: registroResponse.data.restantes
                }));
              }
            } catch (err) {
              console.error('Erro ao registrar consulta Brisanet:', err);
            }

            atualizarResultado('brisanet', {
              success: true,
              data: {
                coverage: coverage,
                message: coverage ? 'Cobertura disponível' : 'Sem cobertura'
              },
              loading: false
            });
          } else {
            throw new Error(apiData.message || 'Erro na resposta da API');
          }
        } catch (err) {
          console.error('Erro na consulta BRISANET:', err);
          atualizarResultado('brisanet', {
            success: false,
            error: err.response?.data?.error || err.message || 'Erro ao consultar cobertura BRISANET',
            loading: false
          });
        }
      })() : null;

      // Processar AONET em paralelo (usa API key do backend)
      const promiseAonet = temAonet ? (async () => {
        try {
          atualizarResultado('aonet', {
            loading: true,
            success: null,
            data: { message: 'Aguardando consulta...' }
          });

          // Fazer requisição via backend (que já gerencia a API key e gera nome/telefone automaticamente)
          const response = await requestWithRetry(() => axios.post('/consultas/aonet', {
            cep,
            numero
          }));

          // Atualizar informações de limite
          if (response.data.limite !== undefined) {
            setLimiteInfo(prev => ({
              ...prev,
              limite: response.data.limite,
              usadas: response.data.usadas,
              restantes: response.data.restantes
            }));
          }

          atualizarResultado('aonet', {
            success: true,
            data: {
              coverage: response.data.coverage,
              message: response.data.message,
              idLead: response.data.idLead,
              products: response.data.products
            },
            loading: false
          });
        } catch (err) {
          console.error('Erro na consulta AONET:', err);
          atualizarResultado('aonet', {
            success: false,
            error: err.response?.data?.error || err.message || 'Erro ao consultar cobertura AONET',
            loading: false
          });
        }
      })() : null;

      // Processar ZAAZ em paralelo (via backend, API Withub)
      const promiseZaaz = temZaaz ? (async () => {
        try {
          atualizarResultado('zaaz', {
            loading: true,
            success: null,
            data: { message: 'Aguardando consulta...' }
          });

          const response = await requestWithRetry(() => axios.post('/consultas/zaaz', {
            cep,
            numero
          }));

          if (response.data.limite !== undefined) {
            setLimiteInfo(prev => ({
              ...prev,
              limite: response.data.limite,
              usadas: response.data.usadas,
              restantes: response.data.restantes
            }));
          }

          atualizarResultado('zaaz', {
            success: true,
            data: {
              coverage: response.data.coverage,
              message: response.data.message,
              idLead: response.data.idLead,
              products: response.data.products
            },
            loading: false
          });
        } catch (err) {
          console.error('Erro na consulta ZAAZ:', err);
          atualizarResultado('zaaz', {
            success: false,
            error: err.response?.data?.error || err.message || 'Erro ao consultar cobertura ZAAZ',
            loading: false
          });
        }
      })() : null;

      // Processar LIGGA em paralelo (via backend, API new-prod sem token)
      const promiseLigga = temLigga ? (async () => {
        try {
          atualizarResultado('ligga', {
            loading: true,
            success: null,
            data: { message: 'Aguardando consulta...' }
          });

          const response = await requestWithRetry(() => axios.post('/consultas/ligga', {
            cep,
            numero
          }));

          if (response.data.limite !== undefined) {
            setLimiteInfo(prev => ({
              ...prev,
              limite: response.data.limite,
              usadas: response.data.usadas,
              restantes: response.data.restantes
            }));
          }

          atualizarResultado('ligga', {
            success: true,
            data: {
              coverage: response.data.coverage,
              message: response.data.message
            },
            loading: false
          });
        } catch (err) {
          console.error('Erro na consulta LIGGA:', err);
          atualizarResultado('ligga', {
            success: false,
            error: err.response?.data?.error || err.message || 'Erro ao consultar cobertura LIGGA',
            loading: false
          });
        }
      })() : null;

      // Aguardar todas as requisições (mas resultados já foram atualizados individualmente)
      const allPromises = [
        ...promises,
        promiseBrisanet,
        promiseAonet,
        promiseZaaz,
        promiseLigga,
        promiseDesktop,
        promiseMHNET,
        promiseAmpernet
      ].filter(p => p !== null);
      await Promise.allSettled(allPromises);
      
      // Recarregar informações de limite após todas as consultas
      loadLimiteInfo();
      
    } catch (err) {
      setError('Erro ao realizar consultas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Função auxiliar para obter estilo da borda com gradiente quando necessário
  const getBorderStyle = (operadora, operadoraColor) => {
    if (operadora === 'aonet') {
      // Azul, Amarelo e Verde
      return {
        border: '3px solid',
        borderImage: 'linear-gradient(135deg, #0066FF 0%, #FFD700 50%, #00CC66 100%) 1',
        borderRadius: '8px'
      };
    }
    if (operadora === 'desktop') {
      // Vermelho e amarelo
      return {
        borderLeft: '4px solid',
        borderImage: 'linear-gradient(135deg, #FF0000 0%, #FFD700 100%) 1',
        borderImageSlice: 1
      };
    } else if (operadora === 'gigamais') {
      // Azul e branco
      return {
        borderLeft: '4px solid',
        borderImage: 'linear-gradient(135deg, #0066FF 0%, #FFFFFF 100%) 1',
        borderImageSlice: 1
      };
    } else if (operadora === 'nio') {
      // Verde e branco
      return {
        borderLeft: '4px solid',
        borderImage: 'linear-gradient(135deg, #00CC66 0%, #FFFFFF 100%) 1',
        borderImageSlice: 1
      };
    } else if (operadora === 'brisanet') {
      // Laranja e branco
      return {
        borderLeft: '4px solid',
        borderImage: 'linear-gradient(135deg, #FF8C00 0%, #FFFFFF 100%) 1',
        borderImageSlice: 1
      };
    } else if (operadora === 'ligga') {
      // Laranja e branco
      return {
        borderLeft: '4px solid',
        borderImage: 'linear-gradient(135deg, #FF8C00 0%, #FFFFFF 100%) 1',
        borderImageSlice: 1
      };
    } else if (operadora === 'uno') {
      return {
        borderLeft: '4px solid #00A3E0'
      };
    } else {
      return {
        borderLeft: `4px solid ${operadoraColor}`
      };
    }
  };

  const renderResultado = (resultado, consultaData) => {
    const { operadora, operadoraName, operadoraColor, success, data, error, loading: isLoading } = resultado;
    const borderStyle = getBorderStyle(operadora, operadoraColor);

    // Se estiver carregando (TIM na fila ou ainda processando, ou BRISANET aguardando token)
    const isTimLoading = operadora === 'tim' && (isLoading || loadingTim);
    const isBrisanetLoading = operadora === 'brisanet' && isLoading;
    const isAonetLoading = operadora === 'aonet' && isLoading;
    const isZaazLoading = operadora === 'zaaz' && isLoading;
    const isLiggaLoading = operadora === 'ligga' && isLoading;
    if (isTimLoading || isBrisanetLoading || isAonetLoading || isZaazLoading || isLiggaLoading || (isLoading && operadora === 'tim')) {
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            padding: '2rem',
            minHeight: '150px'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: `4px solid ${operadoraColor}33`,
              borderTop: `4px solid ${operadoraColor}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '1rem'
            }}></div>
            <p style={{ color: '#999', fontSize: '0.9rem', margin: 0 }}>
              {operadora === 'brisanet' ? 'Obtendo token...' : 'Aguardando na fila...'}
            </p>
          </div>
        </div>
      );
    }

    // Se ainda não recebeu resposta (apenas para outras operadoras, TIM já trata acima)
    if (success === null && operadora !== 'tim') {
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            padding: '2rem',
            minHeight: '150px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: `3px solid ${operadoraColor}33`,
              borderTop: `3px solid ${operadoraColor}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        </div>
      );
    }

    if (success === false) {
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          <div className="alert alert-error">
            <strong>Erro:</strong> {error}
          </div>
        </div>
      );
    }

    // VIVO - mostrar apenas RESULTADO
    if (operadora === 'vivo') {
      const temDisponibilidade = data.RESULTADO && (
        data.RESULTADO.includes('I2402') || 
        data.RESULTADO.includes('Ha disponibilidade para o recurso - Bandwidth') ||
        data.RESULTADO.includes('disponibilidade')
      );
      
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          {data.RESULTADO ? (
            <div className={temDisponibilidade ? "alert alert-success" : "alert alert-info"}>
              <strong>RESULTADO:</strong>{' '}
              <span style={{ color: temDisponibilidade ? '#00ff00' : 'inherit' }}>
                {data.RESULTADO}
              </span>
            </div>
          ) : (
            <div className="alert alert-error">
              <strong>Erro:</strong> Resposta inválida da API
            </div>
          )}
        </div>
      );
    }

    // CLARO - mostrar cobertura
    if (operadora === 'claro') {
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          {data.coverage ? (
            <div className="alert alert-success">
              <strong>✅ COBERTURA DISPONÍVEL</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço possui cobertura da Claro Fibra.'}
              </p>
            </div>
          ) : (
            <div className="alert alert-error">
              <strong>❌ SEM COBERTURA</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço não possui cobertura da Claro Fibra no momento.'}
              </p>
            </div>
          )}
        </div>
      );
    }

    // DESKTOP - mostrar cobertura
    if (operadora === 'desktop') {
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          {data.coverage ? (
            <div className="alert alert-success">
              <strong>✅ COBERTURA DISPONÍVEL</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço possui cobertura Desktop.'}
              </p>
              {data.details && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #333' }}>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                    <strong>Estado:</strong> {data.details.state}
                  </p>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                    <strong>Cidade:</strong> {data.details.city}
                  </p>
                  {data.details.street && (
                    <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                      <strong>Rua:</strong> {data.details.street}
                    </p>
                  )}
                  {data.details.neighborhood && (
                    <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                      <strong>Bairro:</strong> {data.details.neighborhood}
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="alert alert-error">
              <strong>❌ SEM COBERTURA</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço não possui cobertura Desktop no momento.'}
              </p>
              {data.details && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #333' }}>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                    <strong>Estado:</strong> {data.details.state}
                  </p>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                    <strong>Cidade:</strong> {data.details.city}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // TIM - mostrar cobertura
    if (operadora === 'tim') {
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          {data.coverage ? (
            <div className="alert alert-success">
              <strong>✅ COBERTURA DISPONÍVEL</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'TIM Ultrafibra está disponível na sua região!'}
              </p>
            </div>
          ) : (
            <div className="alert alert-error">
              <strong>❌ SEM COBERTURA</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'TIM Ultrafibra não está disponível na sua região no momento.'}
              </p>
            </div>
          )}
        </div>
      );
    }

    // GIGA MAIS - mostrar cobertura
    if (operadora === 'gigamais') {
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          {data.coverage ? (
            <div className="alert alert-success">
              <strong>✅ COBERTURA DISPONÍVEL</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço possui cobertura Giga Mais Fibra.'}
              </p>
              {data.details && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #333' }}>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                    <strong>Estado:</strong> {data.details.state}
                  </p>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                    <strong>Cidade:</strong> {data.details.city}
                  </p>
                  {data.details.street && (
                    <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                      <strong>Rua:</strong> {data.details.street}
                    </p>
                  )}
                  {data.details.neighborhood && (
                    <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                      <strong>Bairro:</strong> {data.details.neighborhood}
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="alert alert-error">
              <strong>❌ SEM COBERTURA</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço não possui cobertura Giga Mais Fibra no momento.'}
              </p>
              {data.details && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #333' }}>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                    <strong>Estado:</strong> {data.details.state}
                  </p>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                    <strong>Cidade:</strong> {data.details.city}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // NIO - mostrar viabilidade
    if (operadora === 'nio') {
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          {data.coverage ? (
            <div className="alert alert-success">
              <strong>✅ VIABILIDADE DISPONÍVEL</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço possui viabilidade NIO.'}
              </p>
              {data.needsNumberCreation && (
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '0.75rem', 
                  backgroundColor: '#331100', 
                  borderRadius: '4px',
                  border: '1px solid #ff6600'
                }}>
                  <p style={{ color: '#ffaa66', margin: 0, fontSize: '0.9rem' }}>
                    <strong>⚠️ Atenção:</strong> É necessário criar o número no sistema da NIO.
                  </p>
                </div>
              )}
              {(data.city || data.district || data.street || data.uf) && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #333' }}>
                  {data.city && (
                    <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                      <strong>Cidade:</strong> {data.city}
                    </p>
                  )}
                  {data.district && (
                    <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                      <strong>Bairro:</strong> {data.district}
                    </p>
                  )}
                  {data.street && (
                    <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                      <strong>Rua:</strong> {data.street}
                    </p>
                  )}
                  {data.uf && (
                    <p style={{ margin: '0.25rem 0', fontSize: '0.85rem', color: '#999' }}>
                      <strong>UF:</strong> {data.uf}
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="alert alert-error">
              <strong>❌ SEM VIABILIDADE</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço não possui viabilidade NIO no momento.'}
              </p>
            </div>
          )}
        </div>
      );
    }

    // BRISANET - mostrar cobertura
    if (operadora === 'brisanet') {
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          {data.coverage ? (
            <div className="alert alert-success">
              <strong>✅ COBERTURA DISPONÍVEL</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço possui cobertura Brisanet.'}
              </p>
            </div>
          ) : (
            <div className="alert alert-error">
              <strong>❌ SEM COBERTURA</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço não possui cobertura Brisanet no momento.'}
              </p>
            </div>
          )}
        </div>
      );
    }

    // LIGGA - mostrar cobertura (apenas mensagem, sem JSON)
    if (operadora === 'ligga') {
      // Garantir que coverage e message estejam definidos
      const coverage = data?.coverage === true;
      const message = data?.message || (coverage ? 'Cobertura disponível' : 'Sem cobertura');
      
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          {coverage ? (
            <div className="alert alert-success">
              <strong style={{ color: '#00ff00' }}>✅ COBERTURA DISPONÍVEL</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0, color: '#00ff00' }}>
                {message}
              </p>
            </div>
          ) : (
            <div className="alert alert-error">
              <strong style={{ color: '#ff0000' }}>❌ SEM COBERTURA</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0, color: '#ff0000' }}>
                {message}
              </p>
            </div>
          )}
        </div>
      );
    }

    // UNO - covered / review / outside
    if (operadora === 'uno') {
      const status = data?.status || (data?.coverage === true ? 'covered' : 'outside');
      const message = data?.message || (
        status === 'covered' ? 'Cobertura disponível' :
        status === 'review' ? 'Cobertura a confirmar' :
        'Sem cobertura'
      );
      const endereco = data?.endereco || {};
      const linhasEndereco = [
        endereco.street,
        [endereco.neighborhood, endereco.city, endereco.state].filter(Boolean).join(' · '),
        data?.area ? `Área: ${data.area}` : null
      ].filter(Boolean);

      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          {status === 'covered' ? (
            <div className="alert alert-success">
              <strong>✅ COBERTURA DISPONÍVEL</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>{message}</p>
              {linhasEndereco.length > 0 && (
                <p style={{ marginTop: '0.5rem', marginBottom: 0, fontSize: '0.9rem' }}>
                  {linhasEndereco.join(' — ')}
                </p>
              )}
            </div>
          ) : status === 'review' ? (
            <div className="alert" style={{ background: '#fff8e6', border: '1px solid #e6a800', color: '#8a6d00' }}>
              <strong>⚠️ COBERTURA A CONFIRMAR</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>{message}</p>
              {linhasEndereco.length > 0 && (
                <p style={{ marginTop: '0.5rem', marginBottom: 0, fontSize: '0.9rem' }}>
                  {linhasEndereco.join(' — ')}
                </p>
              )}
            </div>
          ) : (
            <div className="alert alert-error">
              <strong>❌ SEM COBERTURA</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>{message}</p>
              {linhasEndereco.length > 0 && (
                <p style={{ marginTop: '0.5rem', marginBottom: 0, fontSize: '0.9rem' }}>
                  {linhasEndereco.join(' — ')}
                </p>
              )}
            </div>
          )}
        </div>
      );
    }

    // AONET - mostrar cobertura
    if (operadora === 'aonet') {
      const operadoraInfo = todasOperadoras.find(op => op.id === 'aonet');
      const gradientStyle = operadoraInfo?.gradient 
        ? { background: operadoraInfo.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }
        : { color: operadoraColor };
      
      return (
        <div className="card" style={borderStyle}>
          <h3 style={gradientStyle}>{operadoraName}</h3>
          {data.coverage ? (
            <div className="alert alert-success">
              <strong>✅ COBERTURA DISPONÍVEL</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço possui cobertura Aonet.'}
              </p>
            </div>
          ) : (
            <div className="alert alert-error">
              <strong>❌ SEM COBERTURA</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço não possui cobertura Aonet no momento.'}
              </p>
            </div>
          )}
        </div>
      );
    }

    // ZAAZ - mostrar cobertura
    if (operadora === 'zaaz') {
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor }}>{operadoraName}</h3>
          {data.coverage ? (
            <div className="alert alert-success">
              <strong>✅ COBERTURA DISPONÍVEL</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço possui cobertura ZAAZ.'}
              </p>
            </div>
          ) : (
            <div className="alert alert-error">
              <strong>❌ SEM COBERTURA</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço não possui cobertura ZAAZ no momento.'}
              </p>
            </div>
          )}
        </div>
      );
    }

    // AMPERNET - mostrar cobertura
    if (operadora === 'ampernet') {
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          {data.coverage ? (
            <div className="alert alert-success">
              <strong>✅ COBERTURA DISPONÍVEL</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço possui cobertura AMPERNET.'}
              </p>
            </div>
          ) : (
            <div className="alert alert-error">
              <strong>❌ SEM COBERTURA</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço não possui cobertura AMPERNET no momento.'}
              </p>
            </div>
          )}
        </div>
      );
    }

    // MHNET - mostrar cobertura
    if (operadora === 'mhnet') {
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          {data.coverage ? (
            <div className="alert alert-success">
              <strong>✅ COBERTURA DISPONÍVEL</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço possui cobertura MHNET.'}
              </p>
            </div>
          ) : (
            <div className="alert alert-error">
              <strong>❌ SEM COBERTURA</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço não possui cobertura MHNET no momento.'}
              </p>
            </div>
          )}
        </div>
      );
    }

    // VERO - mostrar cobertura
    if (operadora === 'vero') {
      // Se precisa de telefone do usuário
      if (data.requiresPhone) {
        return (
          <div className="card" style={borderStyle}>
            <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
            <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
              <strong>⚠️ Número de Telefone Necessário</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Não foi possível gerar um número válido automaticamente. Por favor, informe um número de celular válido para continuar a consulta.'}
              </p>
            </div>
            <div className="form-group">
              <label htmlFor={`telefone-vero-${resultado.operadora}`} style={{ marginBottom: '0.5rem', display: 'block' }}>
                Número de Celular (DDD + Número)
              </label>
              <input
                type="text"
                id={`telefone-vero-${resultado.operadora}`}
                value={telefoneUsuario[resultado.operadora] || ''}
                onChange={(e) => {
                  // Permitir apenas números
                  const value = e.target.value.replace(/\D/g, '');
                  setTelefoneUsuario({
                    ...telefoneUsuario,
                    [resultado.operadora]: value
                  });
                }}
                placeholder="Ex: 11987654321"
                maxLength="11"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #333',
                  backgroundColor: '#1a1a1a',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              />
              <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
                Informe o DDD + número do celular (ex: 11987654321)
              </p>
            </div>
            <button
              style={{ backgroundColor: '#cc0000', color: '#fff', border: 'none' }}
            className="btn"
              onClick={async () => {
                const telefone = telefoneUsuario[resultado.operadora];
                if (!telefone || telefone.length < 10) {
                  alert('Por favor, informe um número de celular válido (DDD + número, mínimo 10 dígitos)');
                  return;
                }

                // Atualizar para loading
                atualizarResultado(resultado.operadora, {
                  loading: true,
                  success: null
                });

                try {
                  const response = await requestWithRetry(() => axios.post('/consultas/vero/with-phone', {
                    cep: consultaData.cep.replace(/\D/g, ''),
                    numero: consultaData.numero,
                    telefone: telefone
                  }));

                  // Atualizar informações de limite
                  if (response.data.limite !== undefined) {
                    setLimiteInfo({
                      ...limiteInfo,
                      limite: response.data.limite,
                      usadas: response.data.usadas,
                      restantes: response.data.restantes
                    });
                  }

                  // Atualizar resultado
                  atualizarResultado(resultado.operadora, {
                    success: true,
                    data: response.data,
                    loading: false
                  });

                  // Recarregar informações de limite
                  loadLimiteInfo();
                } catch (err) {
                  atualizarResultado(resultado.operadora, {
                    success: false,
                    error: err.response?.data?.error || 'Erro ao realizar consulta com número fornecido',
                    loading: false
                  });
                }
              }}
              style={{
                width: '100%',
                marginTop: '1rem'
              }}
            >
              Continuar Consulta
            </button>
          </div>
        );
      }

      // Resultado normal (com ou sem cobertura)
      return (
        <div className="card" style={borderStyle}>
          <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
          {data.coverage ? (
            <div className="alert alert-success">
              <strong>✅ COBERTURA DISPONÍVEL</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço possui cobertura VERO.'}
              </p>
            </div>
          ) : (
            <div className="alert alert-error">
              <strong>❌ SEM COBERTURA</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                {data.message || 'Este endereço não possui cobertura VERO no momento.'}
              </p>
            </div>
          )}
        </div>
      );
    }

    // Outras operadoras - mostrar resultado genérico
    return (
      <div className="card" style={{ borderLeft: `4px solid ${operadoraColor}` }}>
        <h3 style={{ color: operadoraColor, marginBottom: '1rem' }}>{operadoraName}</h3>
        {data.message ? (
          <div className="alert alert-info">
            {data.message}
          </div>
        ) : (
          <div className="alert alert-info">
            API ainda não implementada
          </div>
        )}
        {data && Object.keys(data).length > 0 && (
          <pre
            style={{
              background: '#000',
              padding: '1rem',
              borderRadius: '4px',
              overflow: 'auto',
              color: '#ccc',
              fontSize: '0.9rem',
              marginTop: '1rem'
            }}
          >
            {JSON.stringify(data, null, 2)}
          </pre>
        )}
      </div>
    );
  };

  return (
    <div>
      <nav className="nav">
        <Link to="/dashboard" className="nav-link">
          Dashboard
        </Link>
        <Link to="/consultas" className="nav-link active">
          Consultas
        </Link>
        {(JSON.parse(localStorage.getItem('user') || '{}')?.role === 'admin' || 
          JSON.parse(localStorage.getItem('user') || '{}')?.role === 'proprietario') && (
          <Link to="/admin" className="nav-link">
            Administração
          </Link>
        )}
        {(JSON.parse(localStorage.getItem('user') || '{}')?.role === 'admin' || 
          JSON.parse(localStorage.getItem('user') || '{}')?.role === 'proprietario') && (
          <Link to="/limites-consultas" className="nav-link">
            Limites
          </Link>
        )}
      </nav>

      <div className="card">
          <h2 style={{ color: '#cc0000' }}>Consultas de Cobertura</h2>
        <p style={{ color: '#999', marginBottom: '2rem' }}>
          Preencha o CEP e número do endereço para consultar cobertura nas operadoras disponíveis simultaneamente.
          {operadoras.length === 0 && (
            <span style={{ color: '#ff6600', display: 'block', marginTop: '0.5rem' }}>
              ⚠️ Nenhuma operadora disponível. Entre em contato com o administrador.
            </span>
          )}
        </p>

        {/* Card de Limite de Consultas */}
        {limiteInfo && limiteInfo.limite !== null && (
          <div className="card" style={{ marginBottom: '1.5rem', backgroundColor: '#0a0a0a', borderLeft: '4px solid #cc0000' }}>
            <h3 style={{ color: '#cc0000', marginBottom: '0.5rem', fontSize: '1rem' }}>
              Consultas Restantes Este Mês
            </h3>
            <div style={{ marginBottom: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                <span style={{ color: '#999', fontSize: '0.9rem' }}>
                  {limiteInfo.usadas || 0} / {limiteInfo.limite || 0}
                </span>
                <span style={{ 
                  color: limiteInfo.restantes === 0 ? '#ff0000' : limiteInfo.restantes <= limiteInfo.limite * 0.1 ? '#ff6600' : '#00cc66',
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  {limiteInfo.restantes !== null ? limiteInfo.restantes : 'Ilimitado'} restantes
                </span>
              </div>
              <div style={{ 
                width: '100%', 
                height: '20px', 
                backgroundColor: '#333', 
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${Math.min(((limiteInfo.usadas || 0) / limiteInfo.limite) * 100, 100)}%`,
                  height: '100%',
                  backgroundColor: limiteInfo.restantes === 0 ? '#ff0000' : limiteInfo.restantes <= limiteInfo.limite * 0.1 ? '#ff6600' : '#00cc66',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
            </div>
            {limiteInfo.restantes === 0 && (
              <div style={{ 
                padding: '0.75rem', 
                backgroundColor: '#330000', 
                borderRadius: '4px',
                marginTop: '0.5rem'
              }}>
                <p style={{ color: '#ff6666', margin: 0, fontSize: '0.85rem' }}>
                  ⚠️ <strong>Limite de consultas atingido!</strong> O limite será resetado no dia 1 do próximo mês.
                </p>
              </div>
            )}
            {limiteInfo.restantes > 0 && limiteInfo.restantes <= limiteInfo.limite * 0.1 && (
              <div style={{ 
                padding: '0.75rem', 
                backgroundColor: '#331100', 
                borderRadius: '4px',
                marginTop: '0.5rem'
              }}>
                <p style={{ color: '#ffaa66', margin: 0, fontSize: '0.85rem' }}>
                  ⚠️ Você está com menos de 10% do limite restante.
                </p>
              </div>
            )}
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="form-group">
              <label htmlFor="cep">CEP</label>
              <input
                type="text"
                id="cep"
                value={consultaData.cep || ''}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, '');
                  if (value.length > 8) value = value.substring(0, 8);
                  if (value.length > 5) {
                    value = value.substring(0, 5) + '-' + value.substring(5);
                  }
                  setConsultaData({ ...consultaData, cep: value });
                }}
                placeholder="00000-000"
                maxLength="9"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="numero">Número</label>
              <input
                type="text"
                id="numero"
                value={consultaData.numero || ''}
                onChange={(e) => setConsultaData({ ...consultaData, numero: e.target.value })}
                placeholder="Digite o número do endereço"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            style={{ 
              backgroundColor: '#cc0000',
              color: '#fff',
              border: 'none',
              width: '100%',
              opacity: (limiteInfo && limiteInfo.restantes === 0 && limiteInfo.limite !== null) ? 0.5 : 1,
              cursor: (limiteInfo && limiteInfo.restantes === 0 && limiteInfo.limite !== null) ? 'not-allowed' : 'pointer'
            }}
            className="btn"
            disabled={loading || operadoras.length === 0 || (limiteInfo && limiteInfo.restantes === 0 && limiteInfo.limite !== null)}
          >
            {loading 
              ? `Consultando em ${operadoras.length} operadora${operadoras.length > 1 ? 's' : ''}...` 
              : (limiteInfo && limiteInfo.restantes === 0 && limiteInfo.limite !== null)
                ? 'Limite de consultas atingido'
                : operadoras.length === 0 
                  ? 'Nenhuma operadora disponível' 
                  : `Consultar ${operadoras.length} Operadora${operadoras.length > 1 ? 's' : ''}`}
          </button>
        </form>
      </div>

      {loading && (
        <div className="card">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: '#999', fontSize: '1.1rem' }}>
              Consultando em {operadoras.length} operadora{operadoras.length > 1 ? 's' : ''}...
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
              {operadoras.map(op => (
                <div key={op.id} style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: op.color,
                      margin: '0 auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 'bold'
                    }}
                  >
                    {op.name[0]}
                  </div>
                  <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#999' }}>{op.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {resultados && (
        <div>
          <div className="card" style={{ marginBottom: '1rem', backgroundColor: '#0a0a0a' }}>
            <h3 style={{ color: '#cc0000', marginBottom: '1rem' }}>
              Resultados da Consulta
            </h3>
            {consultaData.cep && consultaData.numero && (
              <p style={{ color: '#999', fontSize: '0.9rem' }}>
                CEP: {consultaData.cep.replace(/\D/g, '')} | Número: {consultaData.numero}
              </p>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {resultados.map((resultado, index) => (
              <div key={index}>
                {renderResultado(resultado, consultaData)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Consultas;
